import React, { useCallback, useMemo, useState } from 'react';
import { Flex, PrimaryButton, SafeAreaBottomSpacing } from '@banhala/ui-kit';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import {
  bridge,
  convertToDayJs,
  getCurrentTimeDiff,
  isNil,
  isNotNil,
  isWeb,
} from 'shared/src/utils';
import { NudgeVerticalCarousel } from './components/NudgeVerticalCarousel';
import { LikeButton } from './components/LikeButton';
import { GiftButton } from './components/GiftButton';
import { TopGradientShadow } from './components/TopGradientShadow';
import { TimeSaleBanner } from './components/TimeSaleBanner';
import { ImageBadgeFreeDelivery } from './components/ImageBadgeFreeDelivery';
import { useIsOnSale } from '../../hooks/useIsOnSale';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { useSelectOptionBottomSheet } from '../SelectOptionBottomSheet';
import { usePostGoodsActionBottomSheet } from '../PostGoodsActionBottomSheet';
import { BottomDockedButtonSkeleton } from './BottomDockedButton.skeleton';
import { useMemberSignupRequestModal } from 'shared/src/component';

export const BottomDockedButton: React.FC = () => {
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  const isOnSaleNow = useIsOnSale();
  const [isShowOnce, setIsShowOnce] = useState<boolean>(false);

  const { show: showSelectOptionBottomSheet } = useSelectOptionBottomSheet();
  const { show: showPostGoodsActionBottomSheet } =
    usePostGoodsActionBottomSheet();
  const { show: showMemberSignupRequestModal } = useMemberSignupRequestModal();

  const isDisabled = useMemo(() => {
    if (goodsBasic?.sale_type === 'SOLD_OUT') return true;
    if (goodsBasic?.sale_type === 'STOP_SALE') return true;

    if (goodsBasic?.sale_type === 'SALE_WAIT') {
      return true;
    }
    return false;
  }, [goodsBasic?.sale_type]);

  const isGiftPurchase = useMemo(() => {
    if (isNotNil(goodsBasic)) {
      if (isOnSaleNow) {
        return !goodsBasic.is_overseas_delivery;
      }
    }
    return false;
  }, [goodsBasic, isOnSaleNow]);

  const buttonText = useMemo(() => {
    if (goodsBasic?.sale_type === 'SOLD_OUT') return '품절';
    if (goodsBasic?.sale_type === 'STOP_SALE')
      return '판매가 종료된 상품입니다';
    if (goodsBasic?.sale_type === 'SALE_WAIT') {
      if (isNotNil(goodsBasic?.sale_start_at)) {
        const saleStartTime = convertToDayJs(goodsBasic?.sale_start_at);

        const diffDays = getCurrentTimeDiff(saleStartTime, 'days');
        if (diffDays > 1) {
          return `${saleStartTime.format('MM/dd HH:mm')} 오픈`;
        }

        if (diffDays === 1) {
          return `내일 ${saleStartTime.format('HH:mm')} 오픈`;
        }
        return `오늘 ${saleStartTime.format('HH:mm')} 오픈`;
      }
      return '품절';
    }
    return '구매하기';
  }, [goodsBasic?.sale_start_at, goodsBasic?.sale_type]);

  const onPressRestockNoti = useCallback(() => {
    // bridge.toast('재입고 알림을 작성해주세요');
    setIsShowOnce(true);
    showSelectOptionBottomSheet({ mode: 'purchase', isShowOnce });
  }, [showSelectOptionBottomSheet, isShowOnce]);

  const onPressReserveInquiry = useCallback(() => {
    bridge.toast('예약 문의하기를 작성해주세요');
  }, []);

  const onPressGift = useCallback(() => {
    setIsShowOnce(true);
    showSelectOptionBottomSheet({ mode: 'gift', isShowOnce });
  }, [showSelectOptionBottomSheet, isShowOnce]);

  const onPressPurchase = useCallback(async () => {
    setIsShowOnce(true);
    const result = await showSelectOptionBottomSheet({
      mode: 'purchase',
      isShowOnce,
    });

    if (result.type === 'confirm' && result.data.lastAction === 'addToCart') {
      //addToCart한 뒤에는, BottomSheet으로 상품 추천하기?
      showPostGoodsActionBottomSheet({
        mode: 'cart',
        title: {
          highlight: '장바구니',
          text: '에 상품을 담았어요',
        },
        image: goodsBasic?.cover_images[0] ?? '',
        action: {
          text: '바로가기',
          deeplink: isWeb
            ? `ably://webview?url=https://m.a-bly.com/cart`
            : `ably://webview?title_bar=false&url=cart%3Ffrom%3DRECOMMENDEDMODAL`,
        },
      });
    }
    if (result.type === 'confirm' && result.data.lastAction === 'needSignup') {
      showMemberSignupRequestModal({
        title: '',
        description: '지금 가입하고\n더 많은 상품을 만나보세요',
      }).then(result => {
        if (result.type === 'confirm') {
          bridge.assertLogin({});
        }
      });
    }
  }, [
    isShowOnce,
    goodsBasic?.cover_images,
    showMemberSignupRequestModal,
    showPostGoodsActionBottomSheet,
    showSelectOptionBottomSheet,
  ]);

  if (isNil(goodsDetail)) {
    return <BottomDockedButtonSkeleton />;
  }

  return (
    <Flex>
      {/*<Flex shadowStep={'medium'} />*/}
      <TopGradientShadow />
      {isNotNil(goodsDetail.time_sale_banner) ? (
        <TimeSaleBanner />
      ) : (
        <NudgeVerticalCarousel />
      )}
      {goodsDetail.goods.order_entry_type === 'DEFAULT' ? (
        <Flex.Row
          alignItems={'center'}
          padding={{ horizontal: 12, vertical: 12 }}
        >
          <LikeButton
            initialIsLiked={isNotNil(goodsDetail.goods.like)}
            likesCount={goodsDetail.goods.score.likes_count}
          />
          {isGiftPurchase && <GiftButton onPress={onPressGift} />}
          <Flex.Row flex={1} gap={8}>
            <Flex flex={1}>
              <PrimaryButton
                state={isDisabled ? 'disabled' : undefined}
                variant={'fill'}
                onPress={onPressPurchase}
                testID={'구매하기버튼'}
              >
                {buttonText}
              </PrimaryButton>
              {isOnSaleNow && (
                <Flex position={'absolute'} top={-6} right={8}>
                  <ImageBadgeFreeDelivery></ImageBadgeFreeDelivery>
                </Flex>
              )}
            </Flex>
            {!isOnSaleNow && goodsBasic?.is_possible_applying_restock_noti && (
              <Flex flex={1}>
                <PrimaryButton onPress={onPressRestockNoti} variant={'fill'}>
                  재입고 알림
                </PrimaryButton>
              </Flex>
            )}
          </Flex.Row>
        </Flex.Row>
      ) : (
        <Flex.Row
          alignItems={'center'}
          padding={{ horizontal: 12, vertical: 12 }}
        >
          <LikeButton
            initialIsLiked={isNotNil(goodsDetail.goods.like)}
            likesCount={goodsDetail.goods.score.likes_count}
          />
          <Flex.Row flex={1} gap={8}>
            <Flex flex={1}>
              <PrimaryButton variant={'fill'} onPress={onPressReserveInquiry}>
                예약 문의하기
              </PrimaryButton>
            </Flex>
          </Flex.Row>
        </Flex.Row>
      )}

      <SafeAreaBottomSpacing />
    </Flex>
  );
};
